import { Button, DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import CustomDrawer from "../../../../components/ui/CustomDrawer";
import Loader from "../../../../components/ui/Loader";
import { useAssignSalarySlabMutation } from "../../../../features/organization/contractual/JobDetails/jobDetailsApi";
import { useGetAllSalarySlabQuery } from "../../../../features/organization/contractual/SalarySlab/salarySlabApi";
import { useGetSingleJobAppliedQuery } from "../../../../features/organization/job/jobApi";
import useError from "../../../../hooks/useError";
import { disablePastDates } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { validatePositiveNumber } from "../../../../utils/validators";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";

export default function ManageAssignSlab({
  type,
  open,
  handleClose,
}: {
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const [form] = Form.useForm();
  const { id } = useParams();

  // ------ Queries ------
  const { data: allSalarySlabData, isLoading: allSalarySlabIsLoading } =
    useGetAllSalarySlabQuery({
      params: {
        page: 1,
        limit: 10000000000,
        status: "active",
      },
    });

  const {
    data: singleJobAppliedData,
    isLoading: singleJobAppliedIsLoading,
    isError: singleJobAppliedIsError,
    error: singleJobAppliedError,
  } = useGetSingleJobAppliedQuery(Number(id), {
    skip: !id,
  });

  // ------ Mutations ------
  const [assignSalarySlab, { isLoading, isError, error, isSuccess }] =
    useAssignSalarySlabMutation();

  // ----- Error Handling ------
  useError(isError, error, "Salaray slab assign failed");
  useError(
    singleJobAppliedIsError,
    singleJobAppliedError,
    "Failed to fetch job application details"
  );

  const onFinish = (values: any) => {
    const effectiveMonthWise = dayjs(values?.effective_date)
      .startOf("month")
      .format("YYYY-MM-DD");
    const effectiveDateWise = dayjs(values?.effective_date).format(
      "YYYY-MM-DD"
    );

    const newData = {
      applied_job_id: Number(id),
      salary_slab_id: values?.salary_slab_id,
      effective_date:
        singleJobAppliedData?.data?.jobApplication?.active_contract
          ?.payment_type === "monthly"
          ? effectiveMonthWise
          : effectiveDateWise,
    };

    assignSalarySlab(newData);
  };

  const handleSlabChange = (value: any) => {
    if (!value) {
      // When cleared
      form.setFieldsValue({
        fixed_amount: undefined,
        variable_amount: undefined,
      });
      return;
    }

    const selectedSlab = allSalarySlabData?.data?.salarySlabs?.find(
      (item: any) => item.id === value
    );

    if (selectedSlab) {
      form.setFieldsValue({
        fixed_amount: selectedSlab.fixed_amount,
        variable_amount: selectedSlab.variable_amount,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Another salary slab assigned successfully");
      handleClose();
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <CustomDrawer
        width={460}
        open={open}
        onClose={handleClose}
        title={"Assign Another Salary Slab"}
      >
        {allSalarySlabIsLoading || singleJobAppliedIsLoading ? (
          <Loader />
        ) : (
          <Form
            form={form}
            size="large"
            layout="vertical"
            requiredMark="optional"
            className="custom-form"
            onFinish={onFinish}
          >
            <Form.Item
              name="salary_slab_id"
              label="Select Salary Slab"
              className="mb-4"
              rules={[{ required: true, message: "Please enter select slab" }]}
            >
              <Select
                placeholder="Select Slab"
                size="large"
                allowClear
                showSearch
                suffixIcon={<SelectDropdownLgIcon />}
                onChange={handleSlabChange}
                options={allSalarySlabData?.data?.salarySlabs?.map(
                  (item: any) => ({
                    label: item.slab_name,
                    value: item.id,
                  })
                )}
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="fixed_amount"
                label="Fixed Amount"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter fixed amount" },
                  { validator: validatePositiveNumber },
                ]}
              >
                <Input suffix="BDT" placeholder="0.00" disabled />
              </Form.Item>
              <Form.Item
                name="variable_amount"
                label="Variable Amount"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter variable amount" },
                  { validator: validatePositiveNumber },
                ]}
              >
                <Input suffix="BDT" placeholder="0.00" disabled />
              </Form.Item>
            </div>

            {singleJobAppliedData?.data?.jobApplication?.active_contract
              ?.payment_type === "monthly" ? (
              <Form.Item
                name="effective_date"
                label="Effective Month"
                className="mb-4"
                rules={[
                  { required: true, message: "Please select effective month" },
                ]}
              >
                <DatePicker
                  picker="month"
                  className="w-full"
                  format="MM/YYYY"
                  placeholder={"Select a Month"}
                  suffixIcon={<CalanderIcon />}
                  disabledDate={disablePastDates}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="effective_date"
                label="Effective Date"
                className="mb-4"
                rules={[
                  { required: true, message: "Please select effective date" },
                ]}
              >
                <DatePicker
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder={"Select a Date"}
                  suffixIcon={<CalanderIcon />}
                  disabledDate={disablePastDates}
                />
              </Form.Item>
            )}

            <Flex align="center" gap={16} className="mt-6">
              <Button
                size="middle"
                htmlType="button"
                className="min-w-[120px] h-10"
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                size="middle"
                htmlType="submit"
                type="primary"
                color="primary"
                className="min-w-[120px] h-10"
                loading={isLoading}
              >
                Assign Slab
              </Button>
            </Flex>
          </Form>
        )}
      </CustomDrawer>
    </>
  );
}
